import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from 'react';
import { authAPI } from '../services/api';
import { usersAPI } from '../services/api';
import { profileAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

// Сеть/5xx на старте (перезапуск auth-service или шлюза) — не повод выкидывать на /login:
// повторяем, пока сервис не поднимется. 401/403 — ответ по существу, его не повторяем.
const RETRY_DELAYS_MS = [1000, 2000, 3000, 5000];
const isTransient = (error) =>
  Boolean(error?.isAxiosError) && (!error.response || error.response.status >= 500);

const withTransientRetry = async (fn) => {
  for (let attempt = 0; ; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (!isTransient(error) || attempt >= RETRY_DELAYS_MS.length) throw error;
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt]));
    }
  }
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const { t } = useTranslation(['auth', 'profile']);
  const [user, setUser] = useState(() => {
    // Пробуем получить пользователя из localStorage при инициализации
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [loading, setLoading] = useState(true);

  // Сохраняем пользователя в localStorage при его изменении
  useEffect(() => {
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      localStorage.removeItem('user');
    }
  }, [user]);

  const checkAuth = useCallback(async () => {
    try {
      // /oauth-callback сам обменивает accessToken на refresh-cookie — параллельная
      // попытка silent refresh тут создала бы гонку за тот же токен.
      const pathname = window.location?.pathname || '';
      if (pathname.startsWith('/oauth-callback')) {
        setLoading(false);
        return;
      }

      let token = localStorage.getItem('token');

      // Единый вход между приложениями AnyEmp: сессия, начатая в другом
      // приложении, отсюда не видна (access-токен привязан к origin), а
      // host-only refresh-кука шлюза уезжает с любым запросом с credentials.
      if (!token) {
        try {
          await withTransientRetry(() => authAPI.refreshToken());
          token = localStorage.getItem('token');
        } catch {
          token = null;
        }
      }

      // Если токен всё ещё отсутствует, пользователь не аутентифицирован
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }

      // Пробуем загрузить сохраненного пользователя
      const savedUserJson = localStorage.getItem('user');
      let savedUser = null;

      if (savedUserJson) {
        try {
          savedUser = JSON.parse(savedUserJson);
        } catch (parseError) {
          console.error(
            'checkAuth: Ошибка при парсинге сохраненного пользователя:',
            parseError
          );
          localStorage.removeItem('user');
        }
      }

      // Проверяем токен на валидность через запрос к серверу
      try {
        const response = await withTransientRetry(() => authAPI.validateToken());
        
        if (response?.data && response.data.valid === true) {
          // Если токен валиден, получаем профиль пользователя
          try {
            const profileResponse = await profileAPI.getProfile();
            const profileDataData = profileResponse?.data;
            if (profileDataData && (profileDataData.user || profileDataData._id || profileDataData.id)) {
              // Обработка разных форматов ответа
              const userData = profileDataData.user || profileDataData;
              
              // Преобразуем roles в массив строк с именами ролей
              if (userData.roles && Array.isArray(userData.roles)) {
                userData.roles = userData.roles.map(r => r.name);
              }

              // Проверяем, есть ли у пользователя id
              if (userData._id || userData.id) {
                userData.roles = response.data.user.roles;
                // Обновляем роли в userMeta для совместимости с сайдбаром
                if (userData.userMeta) {
                  userData.userMeta.roles = response.data.user.roles;
                }
                setUser(userData);
                localStorage.setItem('user', JSON.stringify(userData));
              } else {
                console.error('checkAuth: Данные профиля некорректны: отсутствует ID');
                if (savedUser && (savedUser._id || savedUser.id)) {
                  setUser(savedUser);
                } else {
                  localStorage.removeItem('token');
                  localStorage.removeItem('refreshToken');
                  localStorage.removeItem('user');
                  setUser(null);
                }
              }
            } else {
              console.error('checkAuth: Некорректный ответ от сервера профиля:', profileResponse.data);
              throw new Error(t('auth:errors.invalidProfileResponse'));
            }
          } catch (profileError) {
            console.error('checkAuth: Ошибка при получении профиля:', profileError);
            
            // Если есть сохраненный пользователь, используем его
            if (savedUser && (savedUser._id || savedUser.id)) {
              setUser(savedUser);
            } else if (profileError?.response?.status === 401) {
              console.error('checkAuth: Нет сохраненного пользователя, очищаем данные авторизации');
              localStorage.removeItem('token');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('user');
              setUser(null);
            } else {
              // 5xx, таймаут, обрыв сети — профиль не отдали, но сессия жива.
              // Токен не трогаем, иначе временная проблема разлогинит человека.
              setUser(null);
            }
          }
        } else {
          console.error('checkAuth: Токен недействителен, пробуем обновить');
          throw new Error(t('auth:errors.invalidToken'));
        }
      } catch (error) {
        console.error('checkAuth: Ошибка валидации токена:', error);
        
        // Пробуем обновить токен
        try {
          await authAPI.refreshToken();
          // После успешного обновления токена заново проверяем авторизацию
          await checkAuth();
          return;
        } catch (refreshError) {
          console.error('checkAuth: Ошибка обновления токена:', refreshError);
          
          // Если не удалось обновить токен, но у нас есть сохраненный пользователь
          if (savedUser && (savedUser._id || savedUser.id)) {
            // Сессию считаем мёртвой только на явный 401 от рефреша. Раньше
            // отсеивалась лишь строка 'Network Error', а 5xx и таймаут шлюза
            // приводили к разлогину на живой сессии.
            if (refreshError?.response?.status !== 401) {
              setUser(savedUser);
            } else {
              localStorage.removeItem('token');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('user');
              setUser(null);
            }
          } else {
            setUser(null);
          }
        }
      }
    } catch (error) {
      console.error('checkAuth: Общая ошибка проверки аутентификации:', error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Проверяем аутентификацию при монтировании компонента
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login({ email, password });
      
      // Адаптируем структуру ответа к новому формату
      const userData = response.data;

      if (!userData || !userData.tokens || !userData.user) {
        throw new Error(t('auth:errors.invalidLoginResponse'));
      }

      const user = userData.user;

      // Проверяем наличие id в объекте пользователя
      if (!user._id && !user.id) {
        console.error('ОШИБКА: Объект пользователя не содержит id:', user);
        throw new Error(t('auth:errors.invalidUserData'));
      }

      // Преобразуем roles в массив строк с именами ролей
      if (userData.roles && Array.isArray(userData.roles)) {
        userData.roles = userData.roles.map(r => r.name);
      }

      // Токен и refreshToken сохраняются в localStorage в методе authAPI.login
      // setUser(userData.user);

      // Синхронизация пользователя по userId/email
      try {
        const syncRes = await usersAPI.syncOrCreateUser({
          userId: user._id || user.id,
          email: user.email,
          username: user.fullName,
          roles: userData.user.roles,
          avatar: userData.user.avatar
        });

        if (syncRes.data.user) {
          syncRes.data.user.roles = userData.user.roles;
          // Обновляем роли в userMeta для совместимости с сайдбаром
          if (syncRes.data.user.userMeta) {
            syncRes.data.user.userMeta.roles = userData.user.roles;
          }
          setUser(syncRes.data.user);
          localStorage.setItem('user', JSON.stringify(syncRes.data.user));
        }
      } catch (syncErr) {
        console.error('Ошибка синхронизации пользователя:', syncErr);
      }

      toast.success(t('auth:login_success'));
      return userData;
    } catch (error) {
      console.error('Ошибка входа:', error);
      toast.error(error.response?.data?.message || t('auth:login_error'));
      throw error;
    }
  };

  const register = async (fullName, email, password, passwordConfirm) => {
    try {
      // Отправляем поля согласно требованиям сервера аутентификации
      const response = await authAPI.register({
        fullName,
        email,
        password,
        passwordConfirm,
      });
      
      // Адаптируем структуру ответа к новому формату
      const userData = response.data;

      if (!userData || !userData.tokens || !userData.user) {
        throw new Error(t('auth:errors.invalidRegisterResponse'));
      }

      const user = userData.user;

      // Проверяем наличие id в объекте пользователя
      if (!user._id && !user.id) {
        console.error('ОШИБКА: Объект пользователя не содержит id:', user);
        throw new Error(t('auth:errors.invalidUserData'));
      }

      // Преобразуем roles в массив строк с именами ролей
      if (userData.roles && Array.isArray(userData.roles)) {
        userData.roles = userData.roles.map(r => r.name);
      }

      // Токен и refreshToken сохраняются в методе authAPI.register
      setUser(userData);
      toast.success(t('auth:register_success'));
      return userData;
    } catch (error) {
      toast.error(
        error.response?.data?.message || t('auth:register_error')
      );
      throw error;
    }
  };

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
      setUser(null);
      toast.info(t('auth:logout_success'));
    } catch (error) {
      console.error('Ошибка при выходе из системы:', error);
      // Даже при ошибке очищаем локальные данные
      setUser(null);
    }
  }, [t]);

  // Метод для обновления токена
  const refreshToken = async () => {
    try {
      await authAPI.refreshToken();
      return true;
    } catch (error) {
      console.error('Ошибка обновления токена:', error);
      await logout();
      return false;
    }
  };

  const updateUserSettings = async (settings) => {
    try {
      const response = await authAPI.updateProfileSettings(settings);
      // Если нужно, обновляем локальное состояние пользователя
      setUser(prev => prev ? { ...prev, settings: response.data.settings || settings } : prev);
      toast.success(t('profile:settings_saved_success'));
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.message || t('profile:settings_save_error'));
      throw error;
    }
  };

  const updateProfile = async (data) => {
    try {
      let response;
      const formData = new FormData();

      // Добавляем текстовые поля в formData
      formData.append('username', data.username);
      formData.append('email', data.email);
      if (data.bio !== undefined) {
        formData.append('bio', data.bio);
      }

      // Если есть настройки пользователя, добавляем их в formData
      if (data.settings) {
        formData.append('settings', JSON.stringify(data.settings));
      }

      // Если есть файл аватара, добавляем его
      if (data.avatarFile instanceof File) {
        formData.append('avatar', data.avatarFile);
      }

      // Отправка formData вместо простого объекта с данными
      response = await authAPI.updateProfile(formData);

      const updatedUser = response.data.user;

      if (!updatedUser) {
        throw new Error(t('auth:errors.invalidProfileUpdateResponse'));
      }

      // Добавляем полный URL для аватара
      if (
        updatedUser.avatar &&
        !updatedUser.avatar.startsWith('http') &&
        !updatedUser.avatar.startsWith('data:')
      ) {
        updatedUser.avatar = `${process.env.REACT_APP_APP_URL}${updatedUser.avatar}`;
      }

      // Преобразуем roles в массив строк с именами ролей
      if (updatedUser.roles && Array.isArray(updatedUser.roles)) {
        updatedUser.roles = updatedUser.roles.map(r => r.name);
      }

      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      toast.success(t('profile:profile_update_success'));
      return updatedUser;
    } catch (error) {
      toast.error(error.response?.data?.message || t('profile:profile_update_error'));
      throw error;
    }
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    refreshToken,
    updateUserSettings,
    updateProfile,
    loginWithGoogle: authAPI.loginWithGoogle,
    setUser,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  const { t } = useTranslation('auth');
  if (!context) {
    throw new Error(t('errors.useAuthOutsideProvider'));
  }
  return context;
};

export default AuthContext;
